import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useOptimisticAdd } from "../../hooks/useOptimisticAdd";
import { addUserUploadedDocument } from "../../services/apiUser";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FileUpload } from "@/components/component/file-upload";
import { useEffect, useState } from "react";

const FormSchema = z.object({
  type: z.string({ required_error: "Please select a document type" }),
  file: z
    .any()
    .refine((file) => file, "Please upload a document.")
    .refine(
      (file) => {
        if (!file) return true;
        // Check the file type
        const fileType = file.type;
        return (
          fileType === "application/pdf" ||
          fileType ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          fileType === "application/msword"
        );
      },
      {
        message: "Only pdf, docx, and doc files are allowed.",
      }
    ),
});

function DocumentUploadForm() {
  const { mutate } = useOptimisticAdd(
    "uploadedDocuments",
    addUserUploadedDocument
  );
  const form = useForm({
    resolver: zodResolver(FormSchema),
  });

  // store only one file
  const [files, setFiles] = useState([]);

  useEffect(() => {
    form.setValue("file", files?.[0]);
  }, [files, form]);

  function onSubmit(data) {
    const newData = {
      title: data.file.name,
      type: data.type,
      status: "Processing",
    };
    const addData = {
      file: data.file,
      type: data.type,
      // status: "processing",
    };
    mutate({ newData, addData });
  }
  return (
    <Card>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 p-6">
          <FormField
            control={form.control}
            name="file"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel className="text-base">Upload File</FormLabel>
                <FormDescription>
                  Click or drag to upload your file.
                </FormDescription>
                <FormControl>
                  <FileUpload
                    id="cp-profile-doc-upload-file"
                    files={files}
                    setFiles={setFiles}
                    inputProps={{
                      postOnChange: (e) => {
                        field.onChange(e.target.files[0]);
                      },
                      accept: ".pdf,.doc,.docx",
                      multiple: false,
                    }}
                    containerProps={{
                      className: "h-32",
                    }}
                  />
                </FormControl>
                {error && <FormMessage>{error.message}</FormMessage>}
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Select Document Type</FormLabel>
                <TooltipProvider>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger id="cp-profile-doc-upload-type">
                        <SelectValue placeholder="Select the type of your document" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {/* Past Performance Records */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SelectItem value="Past Performance Records">
                            Past Performance Records
                          </SelectItem>
                        </TooltipTrigger>
                        <TooltipContent>
                          Detailed descriptions of previous projects.
                        </TooltipContent>
                      </Tooltip>

                      {/* Capabilities Statement */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SelectItem value="Capabilities Statement">
                            Capabilities Statement
                          </SelectItem>
                        </TooltipTrigger>
                        <TooltipContent>
                          A concise summary of your company’s expertise,
                          services, and unique value propositions.
                        </TooltipContent>
                      </Tooltip>

                      {/* Industry Certifications and Licenses */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SelectItem value="Industry Certifications and Licenses">
                            Industry Certifications and Licenses
                          </SelectItem>
                        </TooltipTrigger>
                        <TooltipContent>
                          Proof of professional qualifications, such as ISO
                          certifications, cybersecurity compliance, or other
                          specialized credentials.
                        </TooltipContent>
                      </Tooltip>

                      {/* Compliance Documentation */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SelectItem value="Compliance Documentation">
                            Compliance Documentation
                          </SelectItem>
                        </TooltipTrigger>
                        <TooltipContent>
                          Documents demonstrating adherence to regulations.
                        </TooltipContent>
                      </Tooltip>

                      {/* Organizational Knowledge Repository */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SelectItem value="Organizational Knowledge Repository">
                            Organizational Knowledge Repository
                          </SelectItem>
                        </TooltipTrigger>
                        <TooltipContent>
                          Includes employee resumes, team structures, and
                          training documentation.
                        </TooltipContent>
                      </Tooltip>

                      {/* Case Studies and White Papers */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SelectItem value="Case Studies and White Papers">
                            Case Studies and White Papers
                          </SelectItem>
                        </TooltipTrigger>
                        <TooltipContent>
                          Detailed reports of successful projects or thought
                          leadership materials that highlight expertise and
                          impact.
                        </TooltipContent>
                      </Tooltip>

                      {/* Other */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SelectItem value="Other">Other</SelectItem>
                        </TooltipTrigger>
                        <TooltipContent>
                          For uncategorized or unique documents that don’t fit
                          into the predefined types.
                        </TooltipContent>
                      </Tooltip>
                    </SelectContent>
                  </Select>
                </TooltipProvider>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            id="cp-profile-doc-upload-submit"
            className="ml-auto w-32"
            type="submit"
          >
            Upload
          </Button>
        </form>
      </Form>
    </Card>
  );
}

export default DocumentUploadForm;
